import { CommonModule } from "@angular/common";

import { Component, EventEmitter, Input, Output } from "@angular/core";

import { FormsModule } from "@angular/forms";

import { finalize } from "rxjs/operators";

import {
  GlogetSalonSetup,
  IntegrationsService,
  SquareLocationSetup,
  SquareSetupResponse,
} from "../../../../../../shared/services/integrations.service";

interface CreateGlogetSalonForm {
  square_location_id: string;

  name: string;
  email: string;
  phone: string;

  address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

@Component({
  selector: "app-square-locations",

  standalone: true,

  imports: [CommonModule, FormsModule],

  templateUrl: "./square-locations.component.html",

  styleUrl: "./square-locations.component.scss",
})
export class SquareLocationsComponent {
  @Input({
    required: true,
  })
  setup!: SquareSetupResponse;

  @Output()
  mappingChanged = new EventEmitter<void>();

  selectedSalonBySquareLocation: Record<string, string> = {};

  mappingLocationId: string | null = null;

  creatingGlogetLocationId: string | null = null;

  creatingSquareSalonId: string | null = null;

  errorMessage = "";

  successMessage = "";

  /*
   * Create-in-gloget modal
   */
  showCreateGlogetModal = false;

  createGlogetSubmitting = false;

  createGlogetError = "";

  selectedSquareLocation: SquareLocationSetup | null = null;

  createGlogetForm: CreateGlogetSalonForm = this.emptyCreateForm();

  constructor(private readonly integrationsService: IntegrationsService) {}

  /* ============================================================
     Data helpers
  ============================================================ */

  get squareLocations(): SquareLocationSetup[] {
    return this.setup?.locations.square ?? [];
  }

  get glogetSalons(): GlogetSalonSetup[] {
    return this.setup?.locations.gloget ?? [];
  }

  get unmappedGlogetSalons(): GlogetSalonSetup[] {
    const mappedSalonIds = new Set(
      this.squareLocations
        .filter((location) => !!location.mapping)
        .map((location) => location.mapping!.salon_id),
    );

    return this.glogetSalons.filter((salon) => !mappedSalonIds.has(salon.id));
  }

  availableSalonsFor(location: SquareLocationSetup): GlogetSalonSetup[] {
    const mappedSalonIds = new Set(
      this.squareLocations
        .filter((item) => !!item.mapping && item.id !== location.id)
        .map((item) => item.mapping!.salon_id),
    );

    return this.glogetSalons.filter((salon) => !mappedSalonIds.has(salon.id));
  }

  /* ============================================================
     Map existing
  ============================================================ */

  mapExisting(location: SquareLocationSetup): void {
    const salonId = this.selectedSalonBySquareLocation[location.id];

    if (!salonId) {
      this.errorMessage = "Select a gloget salon first.";

      return;
    }

    this.mappingLocationId = location.id;

    this.errorMessage = "";

    this.successMessage = "";

    this.integrationsService
      .mapSquareLocation(location.id, salonId)
      .pipe(
        finalize(() => {
          this.mappingLocationId = null;
        }),
      )
      .subscribe({
        next: () => {
          this.successMessage = `${location.name} has been mapped successfully.`;

          this.mappingChanged.emit();
        },

        error: (error) => {
          console.error("Unable to map Square location:", error);

          this.errorMessage =
            error?.error?.detail ?? "Unable to map this Square location.";
        },
      });
  }

  /* ============================================================
     Square -> create gloget salon
  ============================================================ */

  openCreateInGloget(location: SquareLocationSetup): void {
    this.selectedSquareLocation = location;

    this.createGlogetError = "";

    const address = location.address ?? {};

    this.createGlogetForm = {
      square_location_id: location.id,

      name: location.name || location.business_name || "",

      email: this.getSquareBusinessEmail(location),

      phone: location.phone_number || "",

      address: address.address_line_1 || "",

      city: address.city || "",

      state: address.state || "",

      postal_code: address.postal_code || "",

      country: address.country || "",
    };

    this.showCreateGlogetModal = true;
  }

  closeCreateGlogetModal(): void {
    if (this.createGlogetSubmitting) {
      return;
    }

    this.showCreateGlogetModal = false;

    this.selectedSquareLocation = null;

    this.createGlogetError = "";

    this.createGlogetForm = this.emptyCreateForm();
  }

  submitCreateInGloget(): void {
    if (this.createGlogetSubmitting) {
      return;
    }

    this.createGlogetError = "";

    const name = this.createGlogetForm.name.trim();

    const email = this.createGlogetForm.email.trim();

    const phone = this.createGlogetForm.phone.trim();

    if (!name) {
      this.createGlogetError = "Salon name is required.";

      return;
    }

    if (!email) {
      this.createGlogetError = "Email is required.";

      return;
    }

    if (!phone) {
      this.createGlogetError = "Phone number is required.";

      return;
    }

    this.createGlogetSubmitting = true;

    this.integrationsService
      .createGlogetSalonFromSquare({
        square_location_id: this.createGlogetForm.square_location_id,

        name,
        email,
        phone,
      })
      .pipe(
        finalize(() => {
          this.createGlogetSubmitting = false;
        }),
      )
      .subscribe({
        next: () => {
          const locationName = this.selectedSquareLocation?.name || name;

          this.successMessage = `${locationName} was created in gloget and mapped to Square.`;

          this.closeCreateGlogetModal();

          this.mappingChanged.emit();
        },

        error: (error) => {
          console.error("Unable to create gloget salon:", error);

          const detail = error?.error?.detail;

          if (error?.error?.requires_input) {
            const missing = error.error.missing_fields ?? [];

            this.createGlogetError = missing.length
              ? `Please complete: ${missing.join(", ")}.`
              : (detail ?? "Additional information is required.");

            return;
          }

          if (error?.error?.existing_salon) {
            this.createGlogetError =
              `${detail ?? "A matching salon already exists."} ` +
              `Existing salon: ${error.error.existing_salon.name}`;

            return;
          }

          this.createGlogetError =
            detail ?? "Unable to create this salon in gloget.";
        },
      });
  }

  /* ============================================================
     gloget -> create Square location
  ============================================================ */

  createInSquare(salon: GlogetSalonSetup): void {
    const confirmed = window.confirm(
      `Create "${salon.name}" as a new Square location?`,
    );

    if (!confirmed) {
      return;
    }

    this.creatingSquareSalonId = salon.id;

    this.errorMessage = "";

    this.successMessage = "";

    this.integrationsService
      .createSquareLocationFromGloget(salon.id)
      .pipe(
        finalize(() => {
          this.creatingSquareSalonId = null;
        }),
      )
      .subscribe({
        next: () => {
          this.successMessage = `${salon.name} was created in Square and mapped to gloget.`;

          this.mappingChanged.emit();
        },

        error: (error) => {
          console.error("Unable to create Square location:", error);

          if (error?.error?.square_location) {
            this.errorMessage = `${error?.error?.detail ?? "A matching Square location already exists."}`;

            return;
          }

          this.errorMessage =
            error?.error?.detail ?? "Unable to create this location in Square.";
        },
      });
  }

  /* ============================================================
     Formatting
  ============================================================ */

  formatSquareAddress(location: SquareLocationSetup): string {
    const address = location.address;

    if (!address) {
      return "";
    }

    return [
      address.address_line_1,
      address.city,
      address.state,
      address.postal_code,
    ]
      .filter(Boolean)
      .join(", ");
  }

  formatGlogetAddress(salon: GlogetSalonSetup): string {
    return [salon.address, salon.city, salon.state, salon.postal_code]
      .filter(Boolean)
      .join(", ");
  }

  private getSquareBusinessEmail(location: SquareLocationSetup): string {
    /*
     * If you add business_email to the TS interface
     * later this can become:
     *
     * return location.business_email || "";
     *
     * Using a safe cast for compatibility with the
     * current interface.
     */

    return (
      (
        location as SquareLocationSetup & {
          business_email?: string;
        }
      ).business_email ?? ""
    );
  }

  private emptyCreateForm(): CreateGlogetSalonForm {
    return {
      square_location_id: "",

      name: "",
      email: "",
      phone: "",

      address: "",
      city: "",
      state: "",
      postal_code: "",
      country: "",
    };
  }
}
